import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, RotateCcw, Save, Undo2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PhotoUploader } from "../PhotoUploader";

/** One photo slot: changes are staged until the owner presses Save, and the saved state is shown explicitly. */
const SlotCard = ({ slot, onSave, onReset }) => {
  const [draft, setDraft] = useState(slot.img);
  const [state, setState] = useState("saved"); // saved | dirty | saving | error

  useEffect(() => { setDraft(slot.img); setState("saved"); }, [slot.img]);

  const dirty = draft !== slot.img;

  const save = async () => {
    if (!draft) return toast.error("Choose or upload a photo first.");
    setState("saving");
    try {
      await onSave(slot, draft);
      setState("saved");
    } catch (err) {
      setState("error");
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className={`rounded-2xl border bg-parchment p-4 transition-colors ${dirty ? "border-saffron" : "border-inkline"}`} data-testid={`site-photo-${slot.key}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">{slot.page}</div>
          <h4 className="font-display text-base font-semibold text-ink">{slot.label}</h4>
        </div>
        {!dirty && slot.img !== slot.default_img && (
          <button type="button" data-testid={`site-photo-reset-${slot.key}`} onClick={() => onReset(slot)}
            className="inline-flex items-center gap-1.5 rounded-full bg-cream px-3 py-1.5 text-xs font-bold text-ink transition-colors hover:bg-ink hover:text-parchment">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
      <PhotoUploader value={draft} onChange={(img) => { setDraft(img); setState(img !== slot.img ? "dirty" : "saved"); }} folder="site" label="" testid={`site-photo-${slot.key}`} />
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className={`font-mono text-[10px] font-semibold uppercase tracking-[0.2em] ${dirty ? "text-spice" : state === "error" ? "text-spice" : "text-halal"}`} data-testid={`site-photo-state-${slot.key}`}>
          {state === "saving" ? "Saving…" : state === "error" ? "Not saved — try again" : dirty ? "Unsaved changes" : "Saved · live on the site"}
        </span>
        {dirty && (
          <div className="flex gap-2">
            <button type="button" onClick={() => { setDraft(slot.img); setState("saved"); }} data-testid={`site-photo-undo-${slot.key}`}
              className="inline-flex items-center gap-1 rounded-full border border-inkline px-3 py-1.5 text-xs font-semibold text-ink hover:border-spice hover:text-spice">
              <Undo2 size={12} /> Undo
            </button>
            <button type="button" onClick={save} disabled={state === "saving"} data-testid={`site-photo-save-${slot.key}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-spice px-4 py-1.5 text-xs font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60">
              {state === "saving" ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        )}
        {!dirty && state === "saved" && <Check size={14} className="text-halal" />}
      </div>
    </div>
  );
};

export const SitePhotosEditor = () => {
  const [slots, setSlots] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/site-photos");
      setSlots(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (slot, img) => {
    await api.patch(`/admin/site-photos/${slot.key}`, { img });
    setSlots((cur) => cur.map((s) => (s.key === slot.key ? { ...s, img } : s)));
    toast.success(`Saved — ${slot.page} · ${slot.label} is live.`);
  };

  const reset = async (slot) => {
    try {
      const { data } = await api.post(`/admin/site-photos/${slot.key}/reset`);
      setSlots((cur) => cur.map((s) => (s.key === slot.key ? { ...s, img: data.img } : s)));
      toast.success("Restored the original photo.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pages = [...new Set(slots.map((s) => s.page))];

  return (
    <div data-testid="site-photos-editor">
      <p className="mb-5 text-sm text-muted2">
        Swap the hero and gallery photos on the Home and Catering pages. Upload or paste a photo, then press <strong>Save</strong> on that card — the label turns green when it's live.
      </p>
      {pages.map((page) => (
        <section key={page} className="mb-10" data-testid={`site-photos-${page.toLowerCase()}`}>
          <h3 className="mb-4 font-display text-xl font-semibold text-ink">{page} page</h3>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {slots.filter((s) => s.page === page).map((slot) => (
              <SlotCard key={slot.key} slot={slot} onSave={save} onReset={reset} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
